import React, { useEffect, useRef, useState } from 'react';
import './PaymentHistory.css';
import axios from 'axios';
import { API_BASE_URL } from '../../../Constants/ApiEndpoints';
import AddPaymentComponent from '../../../CommonComponents/AddPaymentComponent';
import RefundComponent from '../../../CommonComponents/RefundComponent';
import AddInvoiceComponent from '../../../CommonComponents/AddInvoiceComponent';
import AppointmentReceipt from '../../../CommonComponents/Invoice Receipts/AppointmentReceipt';
import { dateTimeFormatter2 } from '../../../CommonComponents/Utils/DateTimeFormatter';
import {
  FaCreditCard,
  FaReceipt,
  FaStickyNote,
  FaPlus,
  FaPrint,
  FaMoneyBillWave,
  FaUndo,
  FaChevronDown,
  FaChevronUp,
  FaCalendarAlt,
  FaCheckCircle
} from 'react-icons/fa';

const renderInvoiceStatusBadge = (status) => {
  const map = {
    'PAID': { label: 'Paid', cls: 'badge-paid' },
    'UNPAID': { label: 'Unpaid', cls: 'badge-unpaid' },
    'PARTIALLY_PAID': { label: 'Partial Paid', cls: 'badge-partial' },
    'FULLY_REFUNDED': { label: 'Fully Refunded', cls: 'badge-refunded' },
    'PARTIALLY_REFUNDED': { label: 'Partially Refunded', cls: 'badge-refunded' },
    'PENDING': { label: 'Pending', cls: 'badge-pending' }
  };
  const item = map[status] || { label: status || 'Unknown', cls: 'badge-pending' };
  return <span className={`invoice-status-badge ${item.cls}`}>{item.label}</span>;
};

const formatInvoiceNumber = (id) => {
  if (!id) return '';
  const str = String(id);
  if (str.length > 18) {
    return `Invoice #${str.substring(0, 8)}...${str.substring(str.length - 6)}`;
  }
  return `Invoice #${str}`;
};

const formatPaymentNumber = (id) => {
  if (!id) return '';
  const str = String(id);
  if (str.length > 18) {
    return `#${str.substring(0, 8)}...${str.substring(str.length - 6)}`;
  }
  return `#${str}`;
};

const PaymentHistory = ({ patient, refreshKey }) => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedInvoiceId, setExpandedInvoiceId] = useState(null);

  const [showPaymentComponent, setShowPaymentComponent] = useState(false);
  const [showRefundComponent, setShowRefundComponent] = useState(false);
  const [showInvoiceComponent, setShowInvoiceComponent] = useState(false);

  const [selectedInvoice, setSelectedInvoice] = useState(null);

  // For printing invoices BEGIN
  const [printInvoice, setPrintInvoice] = useState(null);
  const [printAllInvoices, setPrintAllInvoices] = useState(false);
  const componentRef = useRef();

  useEffect(() => {
    if (printInvoice || printAllInvoices) {
      setTimeout(() => {
        window.print();
        setPrintInvoice(null);
        setPrintAllInvoices(false);
      }, 150);
    }
  }, [printInvoice, printAllInvoices]);

  const handlePrintClick = (invoice, e) => {
    e.stopPropagation();
    setPrintInvoice(invoice);
  };

  const handlePrintAllClick = () => {
    if (!invoices || invoices.length === 0) {
      alert("No invoices available to print.");
      return;
    }
    setPrintAllInvoices(true);
  };

  const openPaymentComponent = (invoice) => {
    setSelectedInvoice(invoice);
    setShowPaymentComponent(true);
  };

  const closePaymentComponent = () => {
    setShowPaymentComponent(false);
    setSelectedInvoice(null);
  };

  const handlePaymentSubmit = (paymentData) => {
    fetchInvoices();
  };

  const openRefundComponent = (invoice) => {
    setSelectedInvoice(invoice);
    setShowRefundComponent(true);
  };

  const closeRefundComponent = () => {
    setShowRefundComponent(false);
    setSelectedInvoice(null);
  };

  const handleRefundSubmit = (refundData) => {
    fetchInvoices();
  };

  const openInvoiceComponent = () => {
    setShowInvoiceComponent(true);
  };

  const closeInvoiceComponent = () => {
    setShowInvoiceComponent(false);
  };

  const handleInvoiceSubmit = (invoiceData) => {
    closeInvoiceComponent();
    fetchInvoices();
  };

  const fetchInvoices = () => {
    setLoading(true);
    setError(null);

    axios
      .get(`${API_BASE_URL}/getAllInvoicesOfAnAppointment/appointment/${patient.appointmentId}`)
      .then((response) => {
        setInvoices(response.data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message || "Something went wrong");
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchInvoices();
  }, [patient.appointmentId, refreshKey]);

  const toggleInvoice = (invoiceId) => {
    setExpandedInvoiceId(prev => (prev === invoiceId ? null : invoiceId));
  };

  if (loading) return <p className="loading-inv-msg">Loading payment history...</p>;
  if (error) return <p className="error-inv-msg">Error: {error}</p>;

  return (
    <div className="payment-history-container">
      <div className="payment-main-title">
        <FaCreditCard size={16} color="#2563eb" />
        <span>Payments</span>
      </div>

      <div className="payment-header-row">
        <div className="payment-subheading-group">
          <FaReceipt size={15} color="#475569" />
          <h3 className="payment-subheading">Payment History</h3>
        </div>
        <div className="payment-actions">
          <button className="action-btn flex-btn">
            <FaStickyNote size={12} />
            <span>Add Notes</span>
          </button>
          <button className="action-btn flex-btn primary-action-btn" onClick={openInvoiceComponent}>
            <FaPlus size={11} />
            <span>Add Invoice</span>
          </button>
          <button className="action-btn flex-btn" onClick={handlePrintAllClick}>
            <FaPrint size={12} />
            <span>Print All</span>
          </button>
        </div>
      </div>
      <div className="invoice-list">
        {invoices.map((inv, idx) => (
          <div key={idx}>
            <div
              className="invoice-row"
              onClick={() => toggleInvoice(inv.invoiceId)}
              style={{ cursor: 'pointer' }}
            >
              <div className="invoice-left">
                <span className="invoice-number" title={inv.invoiceId}>{formatInvoiceNumber(inv.invoiceId)}</span>
                <span className="invoice-name">({inv.name || "Patient"})</span>
              </div>
              <div className="invoice-status">{renderInvoiceStatusBadge(inv.invoiceStatus)}</div>
              <div className="invoice-right">
                <span className="invoice-amount">
                  ₹{(inv.totalAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
                <button
                  className="icon-action-btn add-pmt-btn"
                  title="Add Payment"
                  onClick={(e) => {
                    e.stopPropagation();
                    openPaymentComponent(inv);
                  }}
                >
                  <FaMoneyBillWave size={13} color="#16a34a" />
                </button>

                <button
                  className="icon-action-btn refund-pmt-btn"
                  title="Refund Invoice"
                  onClick={(e) => {
                    e.stopPropagation();
                    openRefundComponent(inv);
                  }}
                >
                  <FaUndo size={12} color="#2563eb" />
                </button>

                <button
                  className="icon-action-btn print-pmt-btn"
                  title="Print Receipt"
                  onClick={(e) => handlePrintClick(inv, e)}
                >
                  <FaPrint size={12} color="#475569" />
                </button>

                <button
                  className="icon-action-btn expand-pmt-btn"
                  title="Toggle Invoice Details"
                >
                  {expandedInvoiceId === inv.invoiceId ? (
                    <FaChevronUp size={11} color="#64748b" />
                  ) : (
                    <FaChevronDown size={11} color="#64748b" />
                  )}
                </button>
              </div>
            </div>

            {showPaymentComponent && selectedInvoice && (
              <AddPaymentComponent
                invoice={selectedInvoice}
                onClose={closePaymentComponent}
                onSubmit={handlePaymentSubmit}
              />
            )}

            {showRefundComponent && selectedInvoice && (
              <RefundComponent
                invoice={selectedInvoice}
                onClose={closeRefundComponent}
                onSubmit={handleRefundSubmit}
              />
            )}

            {expandedInvoiceId === inv.invoiceId && (
              <div className="invoice-details">
                <div className="inv-date-strip">
                  <FaCalendarAlt size={12} color="#64748b" />
                  <span>Date: <strong>{dateTimeFormatter2(inv.invoiceTime)}</strong></span>
                </div>

                <table className="details-table">
                  <thead>
                    <tr>
                      <th>Particulars</th>
                      <th>Description</th>
                      <th style={{ textAlign: "right" }}>Amount (₹)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td><strong>{inv.invoiceName}</strong></td>
                      <td>{inv.invoiceDescription || '-'}</td>
                      <td style={{ textAlign: "right" }} className="table-amt-cell">
                        ₹{(inv.totalAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                    </tr>
                  </tbody>
                </table>

                <div className="summary-box">
                  <div className="payment-line">
                    {inv.payments && inv.payments.map((pmt, i) => (
                      <div key={i} className="pmt-log-wrapper">
                        {pmt.transactionType === 'REFUND' ? (
                          <div className="pmt-log-item pmt-log-refund">
                            <div className="pmt-log-header">
                              <span className="pmt-badge refund-tag-badge">
                                <FaUndo size={10} /> Refund
                              </span>
                              <span className="pmt-id-text" title={pmt.paymentId}>{formatPaymentNumber(pmt.paymentId)}</span>
                              <span className="pmt-amt-refund">- ₹{(pmt.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                              <span className="pmt-mode-tag">({pmt.paymentMode})</span>
                            </div>
                            <div className="pmt-log-sub">
                              Refunded By <strong>{(pmt.receivedBy || "Staff").toUpperCase()}</strong> at {dateTimeFormatter2(new Date(pmt.paymentDate).toLocaleString())}
                              {pmt.refundReason && <span className="refund-reason-text"> • Reason: {pmt.refundReason}</span>}
                            </div>
                          </div>
                        ) : (
                          <div className="pmt-log-item pmt-log-paid">
                            <div className="pmt-log-header">
                              <span className="pmt-badge paid-tag-badge">
                                <FaCheckCircle size={10} /> Payment
                              </span>
                              <span className="pmt-id-text" title={pmt.paymentId}>{formatPaymentNumber(pmt.paymentId)}</span>
                              <span className="pmt-amt-paid">+ ₹{(pmt.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                              <span className="pmt-mode-tag">({pmt.paymentMode})</span>
                            </div>
                            <div className="pmt-log-sub">
                              Received By <strong>{(pmt.receivedBy || "Staff").toUpperCase()}</strong> at {dateTimeFormatter2(new Date(pmt.paymentDate).toLocaleString())}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  <div className="amount-summary">
                    <div className="summary-row">
                      <span className="summary-label">Total Amount:</span>
                      <span className="summary-val">₹{(inv.totalAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                    <div className="summary-row">
                      <span className="summary-label">Paid Amount:</span>
                      <span className="summary-val paid-color">₹{(inv.amountPaid || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                    {inv.payments && inv.payments.some(p => p.transactionType === 'REFUND') && (
                      <div className="summary-row refund-color">
                        <span className="summary-label">Total Refunded:</span>
                        <span className="summary-val">
                          ₹{inv.payments.filter(p => p.transactionType === 'REFUND').reduce((sum, p) => sum + p.amount, 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </div>
                    )}
                    <div className="summary-row due-row">
                      <span className="summary-label">Due Amount:</span>
                      <span className="summary-val">
                        ₹{((inv.invoiceStatus === 'FULLY_REFUNDED') ? 0 : Math.max(0, inv.totalAmount - inv.amountPaid)).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="collapse-btn-wrapper">
                  <button className="cancel-btn collapse-btn" onClick={() => setExpandedInvoiceId(null)}>
                    <FaChevronUp size={11} />
                    <span>Collapse Details</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Render AppointmentReceipt only when printInvoice is set */}
      {printInvoice && (
        <div className="print-area">
          <AppointmentReceipt ref={componentRef} invoice={printInvoice} patient={patient} />
        </div>
      )}

      {/* Render all receipts when Print All is clicked */}
      {printAllInvoices && (
        <div className="print-area">
          {invoices.map((invItem, i) => (
            <div key={i} className="print-page-break">
              <AppointmentReceipt invoice={invItem} patient={patient} />
            </div>
          ))}
        </div>
      )}

      {showInvoiceComponent && (
        <AddInvoiceComponent
          onClose={closeInvoiceComponent}
          onSubmit={handleInvoiceSubmit}
          appointmentId={patient.appointmentId}
          admissionId={patient.admissionId}
        />
      )}
    </div>

  );
};

export default PaymentHistory;
